<template>
    <div class="py-6">
        <!-- Terms and conditions acceptance (inline in tab) -->
        <template v-if="rentPaymentsData?.needsTos">
            <div class="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                <h3 class="text-lg font-bold text-gray-900 mb-2">Terms and Conditions</h3>
                <form @submit.prevent="submitTos" class="space-y-4">
                    <div class="rounded-md border border-gray-200 bg-gray-50 p-4">
                        <TermsAndConditionsForm :terms="terms" />
                    </div>
                    <div class="flex items-center gap-2">
                        <input
                            id="rent-tos-agree"
                            v-model="tosForm.terms_and_conditions"
                            type="checkbox"
                            class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <label for="rent-tos-agree">
                            I agree to the Terms and Conditions for the rent payments module
                        </label>
                    </div>
                    <p v-if="tosForm.errors.terms_and_conditions" class="text-sm text-red-500">{{ tosForm.errors.terms_and_conditions }}</p>
                    <button
                        type="submit"
                        :disabled="!tosForm.terms_and_conditions"
                        class="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Accept and Continue
                    </button>
                </form>
            </div>
        </template>
        <template v-else-if="rentPaymentsData?.rent_info">
            <!-- Lease summary card -->
            <div class="rounded-xl border border-gray-200 bg-gray-50/50 overflow-hidden">
                <div class="p-6 sm:p-8 flex flex-col sm:flex-row sm:items-stretch gap-6">
                    <div class="flex-1 min-w-0">
                        <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                            <div>
                                <p class="text-sm font-medium text-gray-500 uppercase tracking-wide">Monthly rent</p>
                                <p class="mt-1 text-3xl font-bold text-gray-900">${{ rentPaymentsData.rent_info.rent }}</p>
                                <p class="mt-2 text-sm text-gray-600">
                                    {{ rentPaymentsData.rent_info.lease_start_date }} – {{ rentPaymentsData.rent_info.lease_end }}
                                    <span class="text-gray-400">· Ends {{ rentPaymentsData.rent_info.remaining_months }}</span>
                                </p>
                            </div>
                            <span
                                :class="rentPaymentsData.rent_info.status === 'Active'
                                    ? 'inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800'
                                    : 'inline-flex items-center rounded-full bg-red-100 px-3 py-1 text-sm font-medium text-red-800'"
                            >
                                {{ rentPaymentsData.rent_info.status }}
                            </span>
                        </div>
                        <dl class="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <div>
                                <dt class="text-xs font-medium text-gray-500 uppercase tracking-wide">Rent starts from</dt>
                                <dd class="mt-0.5 text-sm font-medium text-gray-900">{{ rentPaymentsData.rent_info.rent_starts_from }}</dd>
                            </div>
                            <div>
                                <dt class="text-xs font-medium text-gray-500 uppercase tracking-wide">Rent due by</dt>
                                <dd class="mt-0.5 text-sm font-medium text-gray-900">The {{ rentPaymentsData.rent_info.rent_due_date }} of each month</dd>
                            </div>
                        </dl>
                    </div>
                    <div class="sm:border-l sm:border-gray-200 sm:pl-6 flex flex-col gap-2 items-stretch shrink-0 min-w-[14rem]">
                        <!-- RAC-9993: Autopay state machine. Three states:
                             (a) Feature flag off → render nothing (RAC-10011)
                             (b) Active autopay → show next payment + cancel link
                             (c) No autopay → show "Set up Autopay" CTA -->
                        <template v-if="rentPaymentsData?.autopay_enabled">
                            <div v-if="rentPaymentsData?.autopay" class="rounded-md border border-green-200 bg-green-50 p-3">
                                <div class="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-green-700">
                                    <span class="inline-block h-2 w-2 rounded-full bg-green-500"></span>
                                    Autopay Active
                                </div>
                                <p class="mt-1 text-sm text-gray-900">
                                    Next: <span class="font-semibold">${{ rentPaymentsData.autopay.amount_dollars }}</span>
                                    on <span class="font-semibold">{{ rentPaymentsData.autopay.next_payment_date }}</span>
                                </p>
                                <div class="mt-2 flex gap-3 text-xs">
                                    <button
                                        type="button"
                                        @click="confirmSkip"
                                        :disabled="skipping"
                                        class="text-amber-700 hover:underline disabled:opacity-50"
                                    >
                                        {{ skipping ? 'Cancelling…' : 'Cancel this payment' }}
                                    </button>
                                    <Link :href="route('autopay.show', rentPaymentsData?.rented_property_id)" class="text-gray-600 hover:underline">
                                        Manage autopay
                                    </Link>
                                </div>
                            </div>
                            <Link
                                v-else-if="rentPaymentsData?.autopay_available"
                                :href="route('autopay.show', rentPaymentsData?.rented_property_id)"
                                class="theme_color w-full inline-flex items-center justify-center rounded-md text-white py-2 px-3 text-sm font-medium hover:opacity-90 whitespace-nowrap"
                            >
                                Set up Autopay
                            </Link>
                        </template>

                        <Link
                            :href="route('rented-properties.payments', rentPaymentsData?.rented_property_id)"
                            class="theme_color w-full inline-flex items-center justify-center rounded-md text-white py-2 px-3 text-sm font-medium hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-700 transition-opacity whitespace-nowrap"
                        >
                            View current and upcoming payments
                        </Link>
                        <Link
                            :href="route('rented-properties.property-transactions', rentPaymentsData?.property_id)"
                            class="w-full inline-flex items-center justify-center rounded-md border border-gray-300 bg-white py-2 px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-300 transition-colors whitespace-nowrap"
                        >
                            View transactions
                        </Link>
                    </div>
                </div>
                <div class="border-t border-gray-200 bg-amber-50/80 px-6 py-3 sm:px-8">
                    <p class="text-sm text-amber-800">
                        <span class="font-medium">Late charges</span> will be assessed after the {{ rentPaymentsData.rent_info.rent_late_on }} of each month.
                    </p>
                </div>
            </div>
        </template>
        <template v-else>
            <p class="text-gray-500 text-sm">Rent payment information is not available for this property.</p>
        </template>


        <!-- Cancel-this-payment confirmation modal. Replaces window.confirm()
             per the PR review (RentRisk uses in-app modals, not native
             browser dialogs). -->
        <JetstreemModal :show="showSkipModal" :max-width="'md'" @close="showSkipModal = false">
            <div class="px-6 py-4">
                <h2 class="text-lg font-semibold text-gray-900">Cancel this upcoming payment?</h2>
                <p class="mt-2 text-sm text-gray-700">
                    You'll need to pay manually before the due date. Autopay will resume next month automatically.
                </p>
            </div>
            <div class="px-6 py-3 bg-gray-50 flex justify-end space-x-2">
                <button type="button"
                        @click="showSkipModal = false"
                        class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-100">
                    Keep this payment
                </button>
                <button type="button"
                        @click="confirmSkipInModal"
                        :disabled="skipping"
                        class="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50">
                    {{ skipping ? 'Cancelling…' : 'Yes, cancel this payment' }}
                </button>
            </div>
        </JetstreemModal>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { Link, router, useForm } from '@inertiajs/vue3'
import TermsAndConditionsForm from '@/Components/TermsAndConditions/TermsAndConditionsForm.vue'
import JetstreemModal from '@/Jetstream/Modal.vue'

const props = defineProps({
    property: { type: Object, default: null },
    tabData: { type: Object, default: () => ({}) },
    landlord: { type: Object, default: null }, // passed from parent; shown above tabs, not here
    terms: { type: String, default: null },
})

const rentPaymentsData = computed(() => props.tabData?.rentPayments ?? null)
const hasScrolledToBottom = ref(false)
const skipping = ref(false)
const showSkipModal = ref(false)

const tosForm = useForm({
    terms_and_conditions: false,
    intended: typeof window !== 'undefined' && window.route ? window.route('my-property', { tab: 'rent-payments' }) : '',
})

function submitTos() {
    if (!tosForm.terms_and_conditions) return
    tosForm.terms_and_conditions = 1
    const postUrl = typeof window !== 'undefined' && window.route ? window.route('rent-payments.update-terms-and-conditions') : ''
    if (postUrl) tosForm.post(postUrl, { preserveScroll: true })
}

// RAC-9993: cancel the upcoming autopay occurrence so the tenant can pay
// manually for this cycle. Server-side runs Plaid cancel + recreate;
// autopay resumes the following month automatically.
function confirmSkip() {
    if (!rentPaymentsData.value?.autopay) return
    showSkipModal.value = true
}
function confirmSkipInModal() {
    skipping.value = true
    router.patch(route('autopay.skip-next', rentPaymentsData.value.autopay.id), {}, {
        preserveScroll: true,
        onFinish: () => {
            skipping.value = false
            showSkipModal.value = false
        },
    })
}
</script>
